import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import React, { FC, ReactElement, useState, useEffect } from "react";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import DPTableHeaderCell from "Components/DP/DPTable/DPTableHeaderCell/DPTableHeaderCell";
import TablePagination from "@material-ui/core/TablePagination";
import TableFooter from "@material-ui/core/TableFooter";
import mergeClasses from "classnames";
import useStyles from "./styles";

export type LabelProps = {
  label: string;
  align?: "left" | "right" | "center" | undefined;
};

export type Header = string | LabelProps | ReactElement;

interface Props {
  className?: string;
  id?: string;
  headers: Header[];
  colWidths?: number[];
  page?: number;
  rowsPerPage?: number;
  count?: number;
  loading?: boolean;
  onChangePage?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
}

const DPTable: FC<Props> = ({
  className,
  headers,
  id = "",
  page,
  rowsPerPage,
  count,
  onChangePage,
  colWidths,
  loading,
  children,
}) => {
  const selectedPage = page && count ? page - 1 : 0;
  const [currentPage, setCurrentPage] = useState(selectedPage);
  const onChangePageFn = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    setCurrentPage(page);
    if (onChangePage) {
      onChangePage(event, page + 1);
    }
  };
  useEffect(() => {
    setCurrentPage(selectedPage);
  }, [count, page, selectedPage]);

  const classes = useStyles();
  const rootClassName = mergeClasses(classes.root, className);
  return (
    <Table id={id} className={rootClassName}>
      {colWidths && (
        <colgroup>
          {colWidths.map((num, idx) => (
            <col key={`header-width-${idx}`} width={`${num}%`} />
          ))}
        </colgroup>
      )}
      <TableHead>
        <TableRow>
          {headers.map((header, idx) => (
            <DPTableHeaderCell key={`${id}-head-cell-${idx}`} header={header} />
          ))}
        </TableRow>
      </TableHead>
      <TableBody className={classes.tableBody}>{children}</TableBody>
      {rowsPerPage && count ? (
        <TableFooter>
          <TableRow>
            {!loading && (
              <TablePagination
                rowsPerPageOptions={[rowsPerPage]}
                count={count}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                classes={{
                  caption: classes.paginationLabel,
                }}
                labelDisplayedRows={({ from, to, count }) => (
                  <>
                    <b>
                      {from}-{to}
                    </b>{" "}
                    of <b>{count}</b> results
                  </>
                )}
                backIconButtonProps={{
                  classes: {
                    root: classes.paginationButton,
                    disabled: classes.paginationButtonDisabled,
                  },
                  "aria-label": "Previous Page",
                }}
                nextIconButtonProps={{
                  classes: {
                    root: classes.paginationButton,
                    disabled: classes.paginationButtonDisabled,
                  },
                  "aria-label": "Next Page",
                }}
                onChangePage={onChangePageFn}
              />
            )}
          </TableRow>
        </TableFooter>
      ) : null}
    </Table>
  );
};

export default DPTable;
